import React, { useEffect, useMemo } from "react";
import {
  Alert,
  Box,
  Column,
  Columns,
  Radio,
  RadioGroup,
  Stack,
  Text,
  TextLink,
} from "@origin-digital/ods-core";
import { FormValues } from "../types";
import { useFormikContext } from "formik";
import { useResponsiveBelowMd } from "src/hooks";

const COLUMN_SPACE = { xs: "none" as const, md: "xlarge" as const };
const ALERT_TITLE = "You can't request an Internet connection right now";

interface InternetConnectionAlertProps {
  link: string;
  linkText: string;
  handleInternetDeselect: (e: React.MouseEvent<HTMLAnchorElement>) => void;
}

const InternetConnectionAlert: React.FC<InternetConnectionAlertProps> = ({
  link,
  linkText,
  handleInternetDeselect,
}) => (
  <Alert title={ALERT_TITLE}>
    <Text>
      A{" "}
      <TextLink href={link} target="_blank">
        {linkText}
      </TextLink>{" "}
      needs to be raised by customer first.
    </Text>
    <br />
    <Text>
      You can still proceed with requesting other services, but you must{" "}
      <TextLink href="#" onClick={handleInternetDeselect}>
        deselect Internet
      </TextLink>{" "}
      before you can create the premise.
    </Text>
  </Alert>
);

interface QuestionColumnsProps {
  children: React.ReactNode;
  helperText?: string;
}

const QuestionColumns: React.FC<QuestionColumnsProps> = ({
  children,
  helperText,
}) => {
  const isBelowMd = useResponsiveBelowMd();
  return (
    <Columns collapseBelow="md" space={COLUMN_SPACE} alignY="stretch">
      <Column width="2/3">
        <Stack space="medium">{children}</Stack>
      </Column>
      <Column width="1/3">
        {helperText && (
          <Box display="flex" alignItems="stretch" height="full">
            {/* Faux vertical <Divider> (Workaround until we have new variant for <Divider>) */}
            {isBelowMd ? null : (
              <Box
                style={{ borderLeft: "1px solid #E3E3E3" }}
                paddingRight="large"
                height="full"
              />
            )}
            {/* End Faux vertical <Divider> */}
            <Box alignSelf="center" marginTop="xsmall">
              <Stack
                space={{
                  xs: "small",
                  md: "medium",
                }}
              >
                <Text variant="body-small" tone="neutralLight">
                  {helperText}
                </Text>
                {/* If more paragraphs of <Text> required, add here */}
              </Stack>
            </Box>
          </Box>
        )}
      </Column>
    </Columns>
  );
};

const KnockdownRebuildBlock = () => {
  const { values, setFieldValue } = useFormikContext<FormValues>();
  const isShowLocationQuestion = values.questionnaireKnockdown === "yes";
  const isShowDemolitionQuestion =
    isShowLocationQuestion && values.questionnaireLocation === "no";
  const isShowHomePrepareCheckQuestion =
    isShowDemolitionQuestion && values.questionnaireDemolition === "yes";

  const shouldDisablePremiseButton = useMemo(() => {
    const hasInternetWithPreInstall =
      values.selectedConnections.includes("Internet") &&
      (values.internetRequestStatus === "PRE_INSTALL_NBN" ||
        values.internetRequestStatus === "ADDRESS_NOT_FOUND");
    if (!hasInternetWithPreInstall) {
      return false;
    }

    if (values.questionnaireKnockdown === "no") {
      return false;
    }

    if (
      (isShowLocationQuestion && values.questionnaireLocation === "yes") ||
      (isShowDemolitionQuestion && values.questionnaireDemolition === "no") ||
      (isShowHomePrepareCheckQuestion &&
        values.questionnaireHomePrepareCheck === "no")
    ) {
      return true;
    }

    return false;
  }, [
    values.selectedConnections,
    values.internetRequestStatus,
    values.questionnaireKnockdown,
    values.questionnaireLocation,
    values.questionnaireDemolition,
    values.questionnaireHomePrepareCheck,
    isShowLocationQuestion,
    isShowDemolitionQuestion,
    isShowHomePrepareCheckQuestion,
  ]);

  useEffect(() => {
    setFieldValue("isDisablePremiseButton", shouldDisablePremiseButton);
    setFieldValue(
      "isShowInternetSectionInPremise",
      !shouldDisablePremiseButton
    );
  }, [shouldDisablePremiseButton]);

  const handleInternetDeselect = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    setFieldValue(
      "selectedConnections",
      values.selectedConnections.filter(
        (connection) => connection !== "Internet"
      )
    );
  };

  return (
    <Stack
      space={{
        xs: "xlarge",
        md: "xxlarge",
      }}
    >
      {/* Knockdown or rebuild */}
      <QuestionColumns helperText="If you need to remove the existing internet connection established and install a internet new connection since you made changes in your building, then please select as ‘Yes'">
        <RadioGroup
          name="questionnaireKnockdown"
          label="Is a knockdown and rebuild required?"
          value={values.questionnaireKnockdown}
          onChange={(e) => {
            setFieldValue("questionnaireKnockdown", e.currentTarget.value);
            if (e.currentTarget.value === "yes") {
              setFieldValue("questionnaireLocation", "no");
              setFieldValue("questionnaireDemolition", "no");
              setFieldValue("questionnaireHomePrepareCheck", "no");
            }
          }}
        >
          <Radio id="yes" label="Yes" value="yes" />
          <Radio id="no" label="No" value="no" />
        </RadioGroup>
      </QuestionColumns>
      {/* End Knockdown or rebuild */}

      {/* More than 1 location */}
      {isShowLocationQuestion && (
        <QuestionColumns>
          <RadioGroup
            name="questionnaireLocation"
            label="Is a knockdown and rebuild required for more than 1 location?"
            value={values.questionnaireLocation}
            onChange={(e) => {
              setFieldValue("questionnaireLocation", e.currentTarget.value);
              if (e.currentTarget.value === "yes") {
                setFieldValue("questionnaireDemolition", "no");
                setFieldValue("questionnaireHomePrepareCheck", "no");
              }
            }}
          >
            <Radio id="yes" label="Yes" value="yes" />
            <Radio id="no" label="No" value="no" />
          </RadioGroup>
          {values.questionnaireLocation === "yes" && (
            <InternetConnectionAlert
              link="https://www.nbnco.com.au/develop-or-plan-with-the-nbn/new-developments/submit-and-apply"
              linkText="New Application Development Request"
              handleInternetDeselect={handleInternetDeselect}
            />
          )}
        </QuestionColumns>
      )}
      {/* End More than 1 location */}

      {/* demolition */}
      {isShowDemolitionQuestion && (
        <QuestionColumns>
          <RadioGroup
            name="questionnaireDemolition"
            label="Has demolition been completed?"
            value={values.questionnaireDemolition}
            onChange={(e) => {
              setFieldValue("questionnaireDemolition", e.currentTarget.value);
              if (e.currentTarget.value === "yes") {
                setFieldValue("questionnaireHomePrepareCheck", "no");
              }
            }}
          >
            <Radio id="yes" label="Yes" value="yes" />
            <Radio id="no" label="No" value="no" />
          </RadioGroup>
          {values.questionnaireDemolition === "no" && (
            <InternetConnectionAlert
              link="https://www.nbnco.com.au/develop-or-plan-with-the-nbn/relocation-works?utm_source=new_dev_application&utm_medium=development_tile&utm_campaign=building_a_new_home"
              linkText="Relocation works request"
              handleInternetDeselect={handleInternetDeselect}
            />
          )}
        </QuestionColumns>
      )}
      {/* End Demolition */}

      {/* Home Prepare Check */}
      {isShowHomePrepareCheckQuestion && (
        <QuestionColumns>
          <RadioGroup
            name="questionnaireHomePrepareCheck"
            label="Has the home prepare check completed?"
            value={values.questionnaireHomePrepareCheck}
            onChange={(e) =>
              setFieldValue(
                "questionnaireHomePrepareCheck",
                e.currentTarget.value
              )
            }
          >
            <Radio id="yes" label="Yes" value="yes" />
            <Radio id="no" label="No" value="no" />
          </RadioGroup>
          {values.questionnaireHomePrepareCheck === "no" && (
            <InternetConnectionAlert
              link="https://www.nbnco.com.au/develop-or-plan-with-the-nbn/new-developments/design-build-install/lead-in-conduit"
              linkText="Home Prepared Check request"
              handleInternetDeselect={handleInternetDeselect}
            />
          )}
        </QuestionColumns>
      )}
      {/* End Home Prepare Check */}
    </Stack>
  );
};

export default KnockdownRebuildBlock;
